import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { RotatedHeader } from 'coreOld/elements';
import { FormActions } from 'coreOld/enums';
import { getListPayload } from 'coreOld/utils';
import { useGetRanks, useGetUsersRanks, useRanksActions } from 'system/hooks';
import { mapRankDataToSend, mapRemoveRankDataToSend } from 'system/mappers';
import { IRank } from 'system/types';

import { columns } from './columns';
import UsersRanksView from './UsersRanksView';
import UserRankAccess from '../UserRankAccess';

interface IProps {
  filters?: Record<string, any>;
  selectedItem?: FormActions | IRank | null;
  setSelectedItem?: Dispatch<SetStateAction<FormActions | IRank | null>>;
}

const UsersRanks = ({ filters, selectedItem, setSelectedItem }: IProps) => {
  const [removedItem, setRemovedItem] = useState<IRank | null>(null);

  const {
    records: userRanks,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    statusCode,
  } = useGetUsersRanks(getListPayload(filters));

  const {
    records,
    isError: isRanksError,
    isLoading: isRanksLoading,
    isFetching: isRanksFetching,
    statusCode: ranksStatusCode,
  } = useGetRanks();

  const { upsertRank, removeRank } = useRanksActions();

  const tableColumns = useMemo(() => {
    const rankCols = records.map((rank) => ({
      id: rank.rankId,
      Header: <RotatedHeader text={rank.rankName} onClick={() => setSelectedItem(rank)} />,
      Cell: ({ row }: any) => <UserRankAccess rank={rank} accessRank={row.original} />,
      flex: 50,
    }));

    return [...columns, ...rankCols];
  }, [columns, records]);

  const onCloseModal = (reset) => {
    setSelectedItem(null);
    reset({ rankName: '' });
  };

  const onSubmit = (reset: UseFormReset<IRank>) => async (data: IRank) => {
    try {
      await upsertRank(mapRankDataToSend(data));

      onCloseModal(reset);
    } catch (error) {}
  };

  const onRemove = async () => {
    setRemovedItem(selectedItem as IRank);
    setSelectedItem(null);
  };

  const onConfirmRemove = async () => {
    try {
      await removeRank(mapRemoveRankDataToSend(removedItem));

      setRemovedItem(null);
      setSelectedItem(null);
    } catch (error) {}
  };

  const onCancelRemove = () => {
    setSelectedItem(removedItem);
    setRemovedItem(null);
  };

  return (
    <UsersRanksView
      tableColumns={tableColumns}
      data={userRanks}
      isLoading={isLoading || isRanksLoading || (isRanksFetching && !isFetchingNextPage)}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isError={isError || isRanksError}
      statusCode={statusCode || ranksStatusCode}
      selectedItem={selectedItem}
      onCloseModal={onCloseModal}
      onSubmit={onSubmit}
      onRemove={onRemove}
      onConfirmRemove={onConfirmRemove}
      removedItem={removedItem}
      onCancelRemove={onCancelRemove}
    />
  );
};

export default UsersRanks;
